'use client';

import { EPlatform } from '@/utils/enums/platform-enum';
import Image from '@/components/Image';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface NavItem {
  name: string;
  path: string;
  image: string;
  type?: 'default' | 'main'; // 移动端专用：default=普通项，main=主要项（更大）
}

interface ActivePageListNavProps {
  list: NavItem[];
  deviceType: EPlatform;
  onExp?: () => void;
  onClick?: (tabName: string) => void;
}

export default function ActivePageListNav({
  list,
  deviceType,
  onExp,
  onClick,
}: ActivePageListNavProps) {
  const [hasExposed, setHasExposed] = useState(false);
  const navRef = useRef<HTMLUListElement>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isWap = deviceType !== EPlatform.WEB;
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);

  // 曝光监听
  useEffect(() => {
    if (hasExposed || !onExp || !navRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasExposed) {
            setHasExposed(true);
            onExp();
          }
        });
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(navRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasExposed, onExp]);

  // 处理点击
  const handleClick = (index: number) => {
    onClick?.(`导航_${index}`);
  };

  // App内H5的URL参数处理
  const formatPathForApp = (path: string) => {
    if (!inAppH5) return path;

    const appQueryKeys = [
      'app_currency_code',
      'version',
      'store_id',
      'source',
      'anonymous_id',
      'distinct_id',
    ];

    let url = path;
    let hasQuery = path.includes('?');

    appQueryKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        url += hasQuery ? '&' : '?';
        url += `${key}=${value}`;
        hasQuery = true;
      }
    });

    return url;
  };

  if (!list || list.length === 0) {
    return null;
  }

  // PC端渲染
  if (!isWap) {
    return (
      <ul
        ref={navRef}
        className="relative flex my-[24px] items-center justify-center gap-[12px] list-none"
      >
        {list.map((item, index) => {
          const isActive = pathname === item.path;

          return (
            <li key={item.name}>
              <Link
                href={formatPathForApp(item.path)}
                onClick={() => handleClick(index)}
                className={`
                  relative flex items-center justify-center
                  px-[5px] py-[5px] rounded-[50px] border border-black
                  transition-colors duration-200
                  ${isActive ? 'bg-black text-white' : 'bg-white text-primary-black hover:bg-gray-100'}
                `}
                aria-label={item.name}
              >
                {/* 图片容器 */}
                <div className="relative w-[35px] h-[35px] rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
                  <Image
                    src={item.image}
                    alt={item.name}
                    fill
                    className="object-cover"
                    sizes="35px"
                  />
                </div>

                {/* 文字 */}
                <span className="px-[10px] pr-[7px] whitespace-nowrap font-br">{item.name}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    );
  }

  // 移动端渲染
  return (
    <ul
      ref={navRef}
      className="relative flex pt-[12px] pb-[12px] pr-[8px] items-center justify-between list-none"
    >
      {list.map((item, index) => {
        const isActive = pathname === item.path;
        const isMain = item.type === 'main';
        const isDefault = item.type === 'default' || !item.type;

        return (
          <li
            key={item.path}
            className={`relative ${isMain ? 'w-[120px] h-[61px]' : 'w-[58px] h-[63px]'}`}
          >
            <Link
              href={formatPathForApp(item.path)}
              onClick={() => handleClick(index)}
              className="block relative w-full h-full"
              aria-label={item.name}
            >
              {/* 图片 */}
              <Image src={item.image} alt={item.name} fill className="object-cover" sizes="120px" />

              {/* 文字叠加层（仅default类型） */}
              {isDefault && (
                <div
                  className={`
                  absolute top-0 left-0 w-full h-full rounded-[8px]
                  ${isActive ? 'bg-black/30' : ''}
                `}
                >
                  <div className="absolute bottom-0 left-0 w-full h-[31px] flex items-end justify-center pb-[3px] rounded-b-[8px] bg-gradient-to-b from-transparent to-black">
                    <span className="text-white text-[8px] leading-[7px] font-bb text-center">
                      {item.name}
                    </span>
                  </div>
                </div>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
